import { useState, useEffect, useRef } from 'react';
import { useGetChatBotMutation } from '../../features/apis/attendanceApi';
import useCurrentUser from '../../hooks/useCurrentUser';
import DefualtIcon from '../../assets/images/profile.png';
import AiICon from '../../assets/images/chatLogo.svg';
import ReactMarkdown from 'react-markdown';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';

import {
  MainContainer,
  ChatContainer,
  MessageList,
  Message,
  MessageInput,
  ConversationHeader,
  TypingIndicator,
  Avatar,
} from '@chatscope/chat-ui-kit-react';
import { Box, Button } from '@chakra-ui/react';
import { IoChatbubbleEllipses, IoClose } from 'react-icons/io5';
import { IoRefresh } from 'react-icons/io5';
import moment from 'moment';

const QUICK_REPLIES = [
  'How many leave days do I have?',
  'Show my payslip',
  'HR policies',
  'My attendance summary',
];

const MarkdownMessage = ({ content }: { content: string }) => (
  <div style={{ lineHeight: '1.45', fontSize: '13px', whiteSpace: 'normal' }}>
    <ReactMarkdown
      components={{
        p: ({ children }) => (
          <p style={{ margin: '0 0 0.45em 0', padding: 0 }}>{children}</p>
        ),
        ul: ({ children }) => (
          <ul
            style={{
              paddingLeft: '18px',
              margin: '0',
              listStyleType: 'disc',
              listStylePosition: 'outside',
              lineHeight: '1.45',
            }}
          >
            {children}
          </ul>
        ),
        ol: ({ children }) => (
          <ol
            style={{
              paddingLeft: '10px',
              margin: '0',
              listStylePosition: 'outside',
              lineHeight: '1.45',
            }}
          >
            {children}
          </ol>
        ),
        li: ({ children }) => (
          <li style={{ margin: '0', padding: '0 0 0 4px', lineHeight: '1.45' }}>
            {children}
          </li>
        ),
        strong: ({ children }) => (
          <strong style={{ fontWeight: 600 }}>{children}</strong>
        ),
        code: ({ children }) => (
          <code
            style={{
              background: '#f0eeff',
              borderRadius: 4,
              padding: '10px 6px',
              fontSize: '12px',
              fontFamily: 'monospace',
            }}
          >
            {children}
          </code>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  </div>
);

const ErrorMessage = ({ onRetry }: { onRetry: () => void }) => (
  <div style={{ fontSize: '13px', color: '#c0392b' }}>
    <span>Sorry, something went wrong.</span>
    <button
      onClick={onRetry}
      style={{
        marginLeft: '8px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '12px',
        color: '#6e3ef5',
        background: 'none',
        border: '0.5px solid #AFA9EC',
        borderRadius: '12px',
        padding: '2px 10px',
        cursor: 'pointer',
      }}
    >
      <IoRefresh size={12} />
      Retry
    </button>
  </div>
);

const ChatBot: React.FC = () => {
  const user = useCurrentUser();
  const messageListRef = useRef<any>(null);
  const lastQuestionRef = useRef<string>('');

  const [messages, setMessages] = useState<any[]>([
    {
      message: `Hello ${user?.name ?? 'there'}! How can I assist you today?`,
      sender: 'AI',
      sentTime: moment().toString(),
      direction: 'incoming',
      isError: false,
    },
  ]);

  const [userInput, setUserInput] = useState<string>('');
  const [sendMessageToAPI, { data, isLoading, isError }] =
    useGetChatBotMutation();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleOpen = () => setIsOpen(true);
    window.addEventListener('open-tara-chatbot', handleOpen as EventListener);
    return () => window.removeEventListener('open-tara-chatbot', handleOpen as EventListener);
  }, []);

  const sendToAPI = (message: string) => {
    lastQuestionRef.current = message;
    sendMessageToAPI({
      question: message,
      user: user?.name || 'User',
      userId: user?._id,
      lastMessage:
        messages.length > 0 ? messages[messages.length - 1].message : '',
    }).unwrap();
  };

  const handleSendMessage = (message: string) => {
    if (!message.trim()) return;

    setMessages((prev) => [
      ...prev,
      {
        message,
        sender: user?.name || 'User',
        sentTime: moment().toString(),
        direction: 'outgoing',
        isError: false,
      },
    ]);
    setUserInput('');
    sendToAPI(message);
  };

  const handleRetry = () => {
    // Remove the last error message
    setMessages((prev) => prev.filter((_, i) => i !== prev.length - 1));
    sendToAPI(lastQuestionRef.current);
  };

  useEffect(() => {
    if (user?.name) {
      setMessages((prev) => [
        {
          ...prev[0],
          message: `Hello ${user.name}! How can I assist you today?`,
        },
        ...prev.slice(1),
      ]);
    }
  }, [user]);

  useEffect(() => {
    if (data?.response) {
      setMessages((prev) => [
        ...prev,
        {
          message: data.response,
          sender: 'AI',
          sentTime: moment().toString(),
          direction: 'incoming',
          isError: false,
        },
      ]);
    }
  }, [data]);

  useEffect(() => {
    if (isError) {
      setMessages((prev) => [
        ...prev,
        {
          message: '',
          sender: 'AI',
          sentTime: moment().toString(),
          direction: 'incoming',
          isError: true,
        },
      ]);
    }
  }, [isError]);

  return (
    <>
      {!isOpen && (
        <Button
          onClick={() => setIsOpen(true)}
          position="fixed"
          bottom="16px"
          right="16px"
          zIndex={1000}
          borderRadius="full"
          width="56px"
          height="56px"
          bg="white"
          color="white"
          display="flex"
          alignItems="center"
          justifyContent="center"
          boxShadow="0 4px 16px rgba(110, 62, 245, 0.2)"
          border="0.5px solid"
          borderColor="gray.100"
          _hover={{ bg: 'gray.50' }}
        >
          <IoChatbubbleEllipses size={28} color="#6e3ef5" />
        </Button>
      )}

      {isOpen && (
        <Box
          position="fixed"
          bottom={{ base: '20px', md: '30px' }}
          right={{ base: '0', md: '20px' }}
          left={{ base: '0', md: 'auto' }}
          zIndex={1000}
          width={{ base: '100%', md: '440px', lg: '480px' }}
          height={{ base: 'calc(100dvh - 96px)', md: '620px', lg: '660px' }}
          backgroundColor="white"
          borderRadius={{ base: '12px 12px 0 0', md: '12px' }}
          boxShadow="0 8px 32px rgba(110, 62, 245, 0.15)"
          border="0.5px solid"
          borderColor="gray.100"
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <MainContainer
            style={{ borderRadius: 'inherit', height: '100%', border: 'none' }}
          >
            <ChatContainer>
              <ConversationHeader
                style={{
                  backgroundColor: '#ECE8FF',
                  borderBottom: '0.5px solid #d4ccff',
                  padding: '12px 16px',
                }}
              >
                <Avatar name="Tara" size="md" src={AiICon} />
                <ConversationHeader.Content userName="Tara" />
                <ConversationHeader.Actions>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsOpen(false)}
                    minW="auto"
                    p={1}
                  >
                    <IoClose size={22} color="#6e3ef5" />
                  </Button>
                </ConversationHeader.Actions>
              </ConversationHeader>

              <MessageList
                ref={messageListRef}
                typingIndicator={
                  isLoading ? (
                    <TypingIndicator content="Tara is typing..." />
                  ) : null
                }
                style={{ backgroundColor: '#fafafa' }}
              >
                {messages.map((msg, index) => (
                  <Message
                    key={index}
                    model={{ ...msg, message: ' ' }}
                    style={{ padding: '4px 8px' }}
                  >
                    <Message.CustomContent>
                      {msg.isError ? (
                        <ErrorMessage onRetry={handleRetry} />
                      ) : msg.direction === 'incoming' ? (
                        <MarkdownMessage content={msg.message} />
                      ) : (
                        <span style={{ fontSize: '13px' }}>{msg.message}</span>
                      )}
                    </Message.CustomContent>
                    <Avatar
                      name={msg.sender}
                      size="sm"
                      src={String(
                        msg.direction === 'incoming'
                          ? AiICon
                          : user?.avatar || DefualtIcon
                      )}
                    />
                    <Message.Footer
                      sentTime={moment(msg.sentTime).format('h:mm A')}
                    />
                  </Message>
                ))}

                {messages.length === 1 && (
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    gap="8px"
                    p="8px 12px 4px"
                  >
                    {QUICK_REPLIES.map((reply) => (
                      <Box
                        key={reply}
                        as="button"
                        onClick={() => handleSendMessage(reply)}
                        px="12px"
                        py="6px"
                        borderRadius="20px"
                        border="0.5px solid"
                        borderColor="#AFA9EC"
                        bg="white"
                        color="#534AB7"
                        fontSize="12px"
                        cursor="pointer"
                        _hover={{ bg: '#ECE8FF' }}
                        transition="background 0.15s"
                      >
                        {reply}
                      </Box>
                    ))}
                  </Box>
                )}
              </MessageList>

              <MessageInput
                value={userInput}
                onChange={(text) => setUserInput(text)}
                onSend={() => handleSendMessage(userInput)}
                placeholder={
                  isLoading ? 'Tara is thinking…' : 'Ask HR Assistant…'
                }
                attachButton={false}
                disabled={isLoading}
                style={{
                  borderTop: '0.5px solid rgba(0,0,0,0.08)',
                  padding: '10px 12px',
                }}
              />
            </ChatContainer>
          </MainContainer>
        </Box>
      )}
    </>
  );
};

export default ChatBot;
